import { useQuery } from '@tanstack/react-query';
import {
  createFileRoute,
  Outlet,
  redirect,
  useNavigate,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { AppShell } from '@/components/app-shell';
import { ErrorState } from '@/components/feedback/error-state';
import { Loading } from '@/components/ui/loading';
import { onSessionRefused } from '@/lib/api';
import { currentStage, signOut } from '@/lib/auth';
import { idleMinutesFor, watchIdle } from '@/lib/idle';
import { adminQuery, queryClient } from '@/lib/query';
import { sheetSearchSchema } from '@/lib/sheet-url';
import { isWallOnly } from '@/lib/staff';
import { StaffContext } from '@/lib/staff-context';
import { useSessionKeepalive } from '@/lib/use-session-keepalive';

const REASON_BY_CODE: Record<string, 'expired' | 'disabled' | 'not_staff'> = {
  SESSION_EXPIRED: 'expired',
  SESSION_ENDED: 'expired',
  UNAUTHORIZED: 'expired',
  STAFF_DISABLED: 'disabled',
  NOT_STAFF: 'not_staff',
};

/** Everything behind sign-in. A session without its second factor goes back. */
export const Route = createFileRoute('/_app')({
  // `open`, `stab` and `sw` address the record sheets on every signed-in page.
  validateSearch: sheetSearchSchema,
  beforeLoad: async () => {
    const stage = await currentStage();
    if (stage.stage !== 'done') throw redirect({ to: '/sign-in' });
    // A wall-only account never sees the shell: it goes to the wall before
    // anything renders, and before an idle timer could start.
    const me = await queryClient
      .ensureQueryData(adminQuery('session.me', {}))
      .catch(() => null);
    if (me && isWallOnly(me)) throw redirect({ to: '/wall' });
  },
  component: SignedInLayout,
});

async function leaveTo(
  navigate: ReturnType<typeof useNavigate>,
  reason: 'expired' | 'disabled' | 'not_staff' | 'signed_out',
) {
  await signOut();
  queryClient.clear();
  await navigate({ to: '/sign-in', search: { reason } });
}

function SignedInLayout() {
  const navigate = useNavigate();
  const me = useQuery(adminQuery('session.me', {}));

  useEffect(
    () =>
      onSessionRefused(
        (error) =>
          void leaveTo(navigate, REASON_BY_CODE[error.code] ?? 'expired'),
      ),
    [navigate],
  );

  // The idle timer starts once the account is known, and never for a wall.
  const idleExempt = me.data ? isWallOnly(me.data) : true;
  const idleMinutes = idleMinutesFor(me.data?.session?.idle_minutes);
  useEffect(() => {
    if (idleExempt) return;
    return watchIdle(idleMinutes, () => void leaveTo(navigate, 'expired'));
  }, [idleExempt, idleMinutes, navigate]);

  useSessionKeepalive(!idleExempt, idleMinutes);

  if (me.isPending) {
    return (
      <div className="grid min-h-dvh place-items-center">
        <Loading />
      </div>
    );
  }
  if (me.isError) {
    return (
      <div className="grid min-h-dvh place-items-center p-6">
        <ErrorState
          title="Could not load your account."
          reason={me.error.message}
          onRetry={() => me.refetch()}
        />
      </div>
    );
  }

  return (
    <StaffContext value={me.data}>
      <AppShell>
        <Outlet />
      </AppShell>
    </StaffContext>
  );
}
